/*
 * How busy the API layer is. Every request made through use-api is counted, so
 * a screen can wait until whatever mounted behind it has finished its first
 * round of loading — the login page uses it to hold its spinner until the app
 * has something to show.
 *
 * Streams and direct fetch() calls are not counted, which is what we want: a
 * long-lived stream would otherwise keep the app "busy" forever.
 */
let inFlight = 0;
let lastActivity = 0;

export function trackRequest<T>(request: Promise<T>): Promise<T> {
  inFlight += 1;
  lastActivity = Date.now();
  const settle = () => {
    inFlight -= 1;
    lastActivity = Date.now();
  };
  request.then(settle, settle);
  return request;
}

interface QuietOptions {
  /** No request in flight, and none started or finished, for this long. */
  quietMs?: number;
  /** Give whatever just mounted time to start its requests. */
  minMs?: number;
  /** Stop waiting regardless — a slow or polling screen must not trap us. */
  maxMs?: number;
}

export function waitForNetworkQuiet({ quietMs = 400, minMs = 300, maxMs = 8000 }: QuietOptions = {}) {
  const start = Date.now();
  return new Promise<void>((resolve) => {
    const check = () => {
      const now = Date.now();
      const settled = inFlight === 0 && now - lastActivity >= quietMs && now - start >= minMs;
      if (settled || now - start >= maxMs) resolve();
      else window.setTimeout(check, 100);
    };
    check();
  });
}
